// Story 1.5 — mantém estável a `src` de uma imagem servida por URL assinada.
//
// O PROBLEMA (bug de produção, PR #435): o backend assina a URL do proxy a cada
// resposta, então todo poll/refetch entrega `?expires&sig` novos. Passar isso
// direto para `<img src>` faz o browser RE-BAIXAR a imagem a cada render — no
// visualizador isso resetava o zoom; num avatar, faz a foto piscar.
//
// A observação que resolve: o `pathname`
// (`/api/v1/storage/file/usuarios/<company>/<file>/<nome>`) é FIXO por imagem.
// Só a query faz churn. Então congela-se por CAMINHO: adota a URL nova apenas
// quando o pathname muda.
//
// E o outro lado (AC 4 da story 1.5): se a assinatura exibida expirar com a
// tela aberta, a imagem quebraria. O `onError` adota UMA vez a assinatura
// fresca do mesmo pathname. Uma só — sem o guard, uma imagem de fato
// indisponível vira loop de re-download a cada render.
//
// ⚠️ DÉBITO CONHECIDO: `components/file-preview/ImagePreview.tsx` tem uma cópia
// inline desta mesma lógica, anterior a este hook. Unificar os dois é
// follow-up: aquele componente serve chat, atendimentos e portal, e refatorá-lo
// está fora do escopo da story 1.5.
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Identidade estável de uma imagem assinada: `origin + pathname`, ignorando a
 * query (`?expires&sig`), que é o que muda a cada re-assinatura.
 *
 * `data:` URI é curto-circuitado no topo — `new URL("data:...")` NÃO lança, e
 * deixá-lo cair no parser alocaria o base64 inteiro a cada render.
 */
export function stableImageKey(u: string | null | undefined): string {
  if (!u) return "";
  if (u.startsWith("data:")) return u;
  try {
    const p = new URL(u, window.location.origin);
    return p.origin + p.pathname;
  } catch {
    return u;
  }
}

export interface SignedImageUrl {
  /** A URL a passar para `<img src>` — estável enquanto o pathname não mudar. */
  displayUrl: string | null;
  /**
   * `true` quando a imagem falhou E a re-assinatura também falhou. É o sinal
   * para exibir o fallback (iniciais).
   *
   * ⚠️ Quem consome ISTO precisa renderizar um `<img>` PRÓPRIO. O
   * `AvatarImage` do Radix não serve: ele mantém uma máquina de estado
   * interna (cria um `new Image()` fora do DOM e só monta o `<img>` quando
   * carrega), então o `onError` abaixo NUNCA dispararia — o Radix trocaria
   * para o fallback sozinho e a re-assinatura do AC 4 jamais rodaria.
   */
  failed: boolean;
  /** Ligar em `<img onError>`. Tenta a assinatura fresca uma vez por caminho. */
  onError: () => void;
  /** Ligar em `<img onLoad>`. Rearma a tentativa para a próxima expiração. */
  onLoad: () => void;
}

/**
 * @param url URL assinada vinda da API. Muda a cada resposta; tudo bem.
 */
export function useSignedImageUrl(url: string | null | undefined): SignedImageUrl {
  const [displayUrl, setDisplayUrl] = useState<string | null>(url ?? null);
  const [failed, setFailed] = useState(false);
  const triedFreshRef = useRef<string | null>(null);

  useEffect(() => {
    // Adota só quando é OUTRA imagem (pathname novo). O churn de assinatura na
    // mesma imagem é ignorado — é isso que impede o re-download.
    if (stableImageKey(url) !== stableImageKey(displayUrl)) {
      setDisplayUrl(url ?? null);
      triedFreshRef.current = null;
      setFailed(false);
    }
  }, [url, displayUrl]);

  const onError = useCallback(() => {
    const key = stableImageKey(displayUrl);
    // Já tentamos a fresca para este caminho e falhou de novo → desiste.
    // Sem isto, uma foto indisponível re-baixaria a cada render.
    if (triedFreshRef.current === key) {
      setFailed(true);
      return;
    }
    triedFreshRef.current = key;
    if (url && url !== displayUrl) {
      setDisplayUrl(url);
    } else {
      // Não há assinatura mais fresca para tentar — a que falhou já era a
      // atual. Desiste agora em vez de esperar um segundo erro que não virá.
      setFailed(true);
    }
  }, [displayUrl, url]);

  const onLoad = useCallback(() => {
    triedFreshRef.current = null;
    setFailed(false);
  }, []);

  return { displayUrl, failed, onError, onLoad };
}
